import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

/**
 * Live leaderboard. Each participant + their current points + a sparkline series.
 * Returns { rows, lastUpdate, status }.
 *
 * rows: [{ participant, points, byDay: [{date, points}], lastWeekRank, currentRank }]
 */
export function useRealtimeLeaderboard() {
  const [state, setState] = useState({ rows: [], lastUpdate: null, status: 'loading' })

  useEffect(() => {
    if (!supabase) {
      setState({ rows: [], lastUpdate: null, status: 'ready' })
      return
    }

    let cancelled = false

    async function reload() {
      const [{ data: participants }, { data: events }] = await Promise.all([
        supabase.from('participants').select('*').eq('active', true),
        supabase.from('point_events').select('*'),
      ])
      if (cancelled) return

      const byParticipant = new Map()
      for (const p of participants || []) {
        byParticipant.set(p.id, { participant: p, points: 0, byDay: new Map() })
      }
      for (const e of events || []) {
        const bucket = byParticipant.get(e.participant_id)
        if (!bucket) continue
        bucket.points += e.points
        const day = e.created_at.slice(0, 10)
        bucket.byDay.set(day, (bucket.byDay.get(day) || 0) + e.points)
      }

      const rows = Array.from(byParticipant.values()).map(b => ({
        participant: b.participant,
        points: b.points,
        byDay: Array.from(b.byDay.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, points]) => ({ date, points })),
      }))

      rows.sort((a, b) => b.points - a.points || a.participant.name.localeCompare(b.participant.name))
      rows.forEach((r, i) => { r.currentRank = i + 1 })

      setState({ rows, lastUpdate: new Date(), status: 'ready' })
    }

    reload()

    // Unique channel per effect run. Supabase returns the SAME channel object
    // if you reuse a name, and StrictMode + HMR will then try to add `.on`
    // listeners to an already-subscribed channel, which throws and crashes
    // the component tree. Random suffix sidesteps it cleanly.
    const channelName = `realtime-leaderboard-${Math.random().toString(36).slice(2, 10)}`
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'point_events' }, reload)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'participants' }, reload)
      .subscribe()

    return () => {
      cancelled = true
      supabase.removeChannel(channel)
    }
  }, [])

  return state
}
